import React from 'react';
import { Drawer, List, ListItem, ListItemText, ListItemButton, ListItemIcon, IconButton, Box, useMediaQuery, useTheme } from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import { Theme } from '@mui/material/styles';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'; // חץ לפתיחה
import ArrowBackIcon from '@mui/icons-material/ArrowBack'; // חץ לסגירה

interface AppDrawerProps {
  open: boolean;
  toggleDrawer: () => void;
  drawerWidth: number;
  closedWidth: number;
  menuItems: {
    text: string;
    icon: React.ReactNode;
    path: string;
  }[];
}

const drawerStyles = {
  drawer: (open: boolean, drawerWidth: number, closedWidth: number, isMobile?: boolean) => ({
    width: open ? drawerWidth : closedWidth,
    flexShrink: 0,
      '& .MuiDrawer-paper': {
      width: open ? drawerWidth : closedWidth,
      background: 'linear-gradient(135deg,rgb(61, 97, 146) 0%,rgb(44, 146, 139) 100%)',
      color: '#fff',
      transition: (theme: Theme) => theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
      overflowX: 'hidden',
      overflowY: 'hidden',
      height: isMobile ? '100%' : 'calc(100% - 64px)',
      marginTop: isMobile ? 0 : '64px',
      position: 'fixed',
      borderLeft: '1px solid rgba(255, 255, 255, 0.1)',
      boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.15)',
      zIndex: isMobile ? (theme: Theme) => theme.zIndex.drawer + 2 : undefined, // Ensure drawer is above AppBar on mobile
      display: 'flex',
      flexDirection: 'column',
      // הסתרת סרגל הגלילה
      scrollbarWidth: 'none', // Firefox
      '&::-webkit-scrollbar': {
        display: 'none', // Chrome, Safari, Edge
      },
    },
  }),
  toggleButton: (isMobile?: boolean) => ({
    display: 'flex',
    justifyContent: 'flex-end',
    padding: '16px',
    marginTop: isMobile ? '56px' : '8px', // Add top margin on mobile to account for AppBar height
  }),
  iconButton: {
    color: '#fff',
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    borderRadius: '12px',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.25)',
      transform: 'scale(1.1)',
    },
  },
  listItemIcon: (open: boolean) => ({
    minWidth: 0,
    justifyContent: 'center',
    color: '#fff',
  }),
  listItemText: (open: boolean) => ({
    opacity: open ? 1 : 0,
    transition: (theme: Theme) => theme.transitions.create('opacity', {
      duration: theme.transitions.duration.enteringScreen,
    }),
    textAlign: 'right',
    ...(open ? { flex: 1 } : { flex: 0, minWidth: 0, width: 0, overflow: 'hidden' }),
  }),
  listItemButton: (open: boolean, isActive: boolean) => ({
    height: 48,
    minHeight: 48,
    maxHeight: 48,
    justifyContent: open ? 'space-between' : 'center',
    display: 'flex',
    flexDirection: 'row',
    px: open ? 2.5 : 1.5,
    my: 0.5,
    mx: 1,
    borderRadius: '12px',
    backgroundColor: isActive ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
    transition: 'all 0.3s ease',
    flexShrink: 0,
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.25)',
      // ב-RTL תנועה טבעית היא ימינה (לא שמאלה)
      transform: 'translateX(4px)',
    },
    ...(isActive && {
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
      fontWeight: 700,
    }),
  }),
};

const AppDrawer: React.FC<AppDrawerProps> = React.memo(({ 
  open, 
  toggleDrawer, 
  drawerWidth, 
  closedWidth, 
  menuItems 
}) => {
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Adjust drawer styles for mobile
  const mobileDrawerWidth = 280;
  const mobileClosedWidth = 0;

  return (
    <Drawer
      sx={drawerStyles.drawer(open, isMobile ? mobileDrawerWidth : drawerWidth, isMobile ? mobileClosedWidth : closedWidth, isMobile)}
      variant={isMobile ? "temporary" : "permanent"}
      anchor="right"
      open={isMobile ? open : true}
      onClose={isMobile ? toggleDrawer : undefined}
      SlideProps={{
        // RTL: מגירה מימין צריכה להיכנס מימין לשמאל.
        direction: 'left',
      }}
      ModalProps={{
        keepMounted: true, // Better open performance on mobile
      }}
    >
      {/* כפתור החץ - נמצא שורה מעל האייקונים */}
      <Box sx={drawerStyles.toggleButton(isMobile)}>
        <IconButton
          onClick={toggleDrawer}
          sx={drawerStyles.iconButton}
        >
          {open ? <ArrowForwardIcon /> : <ArrowBackIcon />}  
        </IconButton>
      </Box>

      {/* רשימת פריטים – גובה קבוע כשהסרגל סגור (לא מתפרסים למטה) */}
      <Box sx={{ 
        flex: 1, 
        overflowY: 'auto', 
        overflowX: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        // הסתרת סרגל הגלילה
        scrollbarWidth: 'none', // Firefox
        '&::-webkit-scrollbar': {
          display: 'none', // Chrome, Safari, Edge
        },
      }}>
        <List sx={{ py: 0, flexShrink: 0, width: '100%' }}>
          {menuItems.map((item, index) => {
            const isActive = location.pathname === item.path;
            
            return (
              <ListItem key={index} disablePadding>
                <ListItemButton
                  component={Link}
                  to={item.path}
                  state={
                    item.path === '/ServiceCallsPage'
                      ? { clearServiceCallsFilters: true }
                      : undefined
                  }
                  sx={drawerStyles.listItemButton(open || isMobile, isActive)}
                  onClick={isMobile ? toggleDrawer : undefined}
                >
                  <ListItemText 
                    primary={item.text} 
                    sx={drawerStyles.listItemText(open || isMobile)}
                  />
                  <ListItemIcon
                    sx={drawerStyles.listItemIcon(open || isMobile)}
                  >
                    {item.icon}
                  </ListItemIcon>
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Box>
    </Drawer>
  );
});

AppDrawer.displayName = 'AppDrawer';

export default AppDrawer;